import { mobileQuery } from '@globals/themes/themeConstants'
import { useCurrentMember } from '@globals/useCurrentMember'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTenantGifsEnabled } from '@shared/hooks/useTenantGifPreference'
import { useT } from '@transifex/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, FieldValues, Path, useFormContext } from 'react-hook-form'
import { buildRichTextEditorEvents } from './richTextEditorEvents'
import type { RichTextEditor } from './types'
import useEditorButtons from './useEditorButtons'
import useFroalaAttachments, { AttachmentUploadFn } from './useFroalaAttachments'
import useFroalaGifs from './useFroalaGifs'
import { useFroalaMentions } from './useFroalaMentions'
import useRichTextImageUpload, { ImageUploadFn } from './useRichTextImageUpload'

type UseRichTextEditorFieldParams<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  autoFocus: boolean
  placeholder?: string
  disabled?: boolean
  minHeight: number
  imageUploadFn?: ImageUploadFn
  areGifsSupported: boolean
  areMentionsSupported: boolean
  attachmentUploadFn?: AttachmentUploadFn
  attachmentFieldName: string
  gifFieldName: string
  gifModalId: string
  isFullVersion: boolean
  charCounterMax: number
  compactUntilFocus: boolean
  onExpandedChange?: (expanded: boolean) => void
  onEnterSubmit?: (currentValue: string) => void
  isCompactEditorExpanded?: boolean
}

/**
 * Owns RichTextEditorField's state: compact-mode expansion and its effects, the image, GIF,
 * attachment and mention plugin hooks, and the memoised Froala config the editor mounts with.
 * Params are the post-default prop values; the component keeps the defaults.
 */
export const useRichTextEditorField = <T extends FieldValues>({
  name,
  control,
  autoFocus,
  placeholder,
  disabled,
  minHeight,
  imageUploadFn,
  areGifsSupported,
  areMentionsSupported,
  attachmentUploadFn,
  attachmentFieldName,
  gifFieldName,
  gifModalId,
  isFullVersion,
  charCounterMax,
  compactUntilFocus,
  onExpandedChange,
  onEnterSubmit,
  isCompactEditorExpanded,
}: UseRichTextEditorFieldParams<T>) => {
  const t = useT()
  // When compactUntilFocus is set, seed the expanded state from the controlled
  // isCompactEditorExpanded prop so a parent that mounts already-expanded (e.g.
  // CommentFormLoader's autoExpand) starts expanded. Otherwise the collapse
  // effect below would fire onExpandedChange(false) on mount and clobber the
  // parent's expanded state.
  const [isExpanded, setIsExpanded] = useState(
    compactUntilFocus ? (isCompactEditorExpanded ?? false) : true,
  )
  const { upload } = useRichTextImageUpload(imageUploadFn)
  const { member: tenantMember } = useCurrentMember()
  const isMobile = useMediaQuery(mobileQuery)
  const editorRef = useRef<RichTextEditor | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const [editorInstanceForMentions, setEditorInstanceForMentions] = useState<RichTextEditor | null>(
    null,
  )
  const onChangeRef = useRef<((html: string) => void) | null>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isCompact = compactUntilFocus && !isExpanded

  const { setError, clearErrors } = useFormContext()
  const setErrorRef = useRef(setError)
  const clearErrorsRef = useRef(clearErrors)
  setErrorRef.current = setError
  clearErrorsRef.current = clearErrors

  // Enables setting isExpanded=false after form with compactUntilFocus is submitted
  useEffect(() => {
    if (compactUntilFocus && isCompactEditorExpanded !== undefined) {
      setIsExpanded(isCompactEditorExpanded)
    }
  }, [isCompactEditorExpanded, compactUntilFocus])

  // Clear pending blur timeout on unmount
  useEffect(() => {
    return () => {
      // blurTimeoutRef holds a timer id that the blur handler in richTextEditorEvents.ts assigns,
      // not a DOM node, so the cleanup must read the latest id rather than a mount-time copy.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  // When expanded with compactUntilFocus, close on click outside the editor.
  // Clicks on the mentions popup (.tribute-container) are not considered "outside" so the editor stays expanded.
  useEffect(() => {
    if (!compactUntilFocus || !isExpanded) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isOutsideEditor =
        editorContainerRef.current && !editorContainerRef.current.contains(target)
      const isOnMentionsPopup = (target as Element).closest?.('.tribute-container')
      if (isOutsideEditor && !isOnMentionsPopup) {
        setIsExpanded(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [compactUntilFocus, isExpanded])

  // Notify parent when collapsed (immediate). "Expanded" is notified from initialized() once the editor is ready to avoid button flicker during expand.
  useEffect(() => {
    if (!isExpanded) {
      onExpandedChange?.(false)
    }
  }, [isExpanded, onExpandedChange])
  // Gating the tenant setting here rather than at each call site means a new
  // editor inherits it automatically. The setting can only take GIFs away from a
  // call site that asked for them, never add them to one that didn't.
  const { gifsEnabled, isLoading: isGifPreferenceLoading } = useTenantGifsEnabled({
    enabled: areGifsSupported,
  })
  // The preference reads as enabled until it resolves, so wait it out rather
  // than optimistically claiming the global GIF callback and pulling in the
  // Giphy chunk for a tenant that turns out to have GIFs off.
  const areGifsAvailable = areGifsSupported && !isGifPreferenceLoading && gifsEnabled

  const {
    moreTextButtons,
    moreParagraphButtons,
    moreRichButtons,
    moreMiscButtons,
    pluginsEnabled,
    shortcutsEnabled,
  } = useEditorButtons(
    isFullVersion,
    attachmentUploadFn,
    areGifsAvailable,
    isCompact,
    imageUploadFn,
  )

  // Use GIF hook for all GIF-related functionality
  const { giphyDialogId, handleGifSelected, registerGifButtonCommand } = useFroalaGifs({
    fieldName: gifFieldName,
    modalId: gifModalId,
    isEnabled: areGifsAvailable,
  })

  // Use attachment hook if attachmentUploadFn is provided
  // Note: This hook requires FormProvider context, so it will only work in forms
  // IMPORTANT: Pass the exact same control instance that AttachmentsField uses
  // Don't cast it - pass it as-is and let the hook handle the typing
  const controlForAttachments = control as unknown as Control<FieldValues>

  const attachmentHook = useFroalaAttachments(
    attachmentUploadFn
      ? {
          mutationFn: attachmentUploadFn,
          fieldName: attachmentFieldName,
          control: controlForAttachments,
        }
      : undefined,
  )

  // Initialize mentions if enabled (use state so hook runs after editor initializes; ref alone doesn't trigger re-render)
  useFroalaMentions(editorInstanceForMentions, tenantMember?.id, areMentionsSupported)

  const visibleToolbarButtons = useCallback(() => {
    const buttons = isMobile
      ? moreTextButtons.filter((btn: string) => btn !== 'emoticons')
      : moreTextButtons

    return !!onEnterSubmit
      ? buttons.filter((btn: string) => btn !== 'formatOLSimple' && btn !== 'formatUL')
      : buttons
  }, [isMobile, moreTextButtons, onEnterSubmit])

  const visibleMoreRichButtons = isMobile
    ? moreRichButtons.filter((btn: string) => btn !== 'insertTable')
    : moreRichButtons

  const editorConfig = useMemo(() => {
    if (areGifsAvailable) {
      registerGifButtonCommand()
    }
    attachmentHook.registerAttachmentButtonCommand()

    return {
      /* NOTE: This prevents dragging and dropping files into RichTextEditorField.
       * Attachment upload via registerAttachmentButtonCommand() is preserved.
       */
      fileUpload: false,
      videoUpload: false,
      /* NOTE: This exclues imageByURL from imageInsertButtons.
       * Support for imageByURL is not added yet.
       */
      imageInsertButtons: [
        'imageBack',
        'imageUpload',
        'imageManager',
        // 'imageByURL'
      ],
      imagePaste: false,
      imageAllowedTypes: ['jpeg', 'jpg', 'png', 'webp'],
      imageMaxSize: 10 * 1024 * 1024,
      imageEditButtons: [
        'imageAlign',
        // 'imageAlt',
        // 'imageLink',
        'imageCaption',
        'imageDisplay',
        'imageRemove',
        'imageReplace',
        // 'imageSize',
        'imageStyle',
      ],
      placeholderText: placeholder,
      attribution: false,
      emoticonsUseImage: false,
      charCounterCount: false,
      autoFocus,
      toolbarSticky: false,
      toolbarButtons: {
        moreText: {
          buttons: visibleToolbarButtons(),
          buttonsVisible: isMobile ? 4 : 7,
        },
        moreParagraph: {
          buttons: moreParagraphButtons,
        },
        moreRich: {
          buttons: visibleMoreRichButtons,
        },
        moreMisc: {
          buttons: moreMiscButtons,
          align: 'right',
          buttonsVisible: 2,
        },
      },
      pluginsEnabled,
      shortcutsEnabled,
      heightMin: isCompact ? 56 : minHeight,
      events: buildRichTextEditorEvents({
        name,
        t,
        charCounterMax,
        imageUploadFn,
        upload,
        setErrorRef,
        clearErrorsRef,
        onChangeRef,
        editorRef,
        blurTimeoutRef,
        setEditorInstanceForMentions,
        isExpanded,
        setIsExpanded,
        compactUntilFocus,
        onExpandedChange,
        onEnterSubmit,
        areGifsAvailable,
        registerGifButtonCommand,
        attachmentUploadFn,
        attachmentHook,
        disabled,
        autoFocus,
      }),
    }
  }, [
    placeholder,
    disabled,
    name,
    upload,
    attachmentHook,
    imageUploadFn,
    registerGifButtonCommand,
    moreParagraphButtons,
    moreMiscButtons,
    pluginsEnabled,
    shortcutsEnabled,
    autoFocus,
    charCounterMax,
    compactUntilFocus,
    isExpanded,
    isCompact,
    onExpandedChange,
    onEnterSubmit,
    t,
    areGifsAvailable,
    attachmentUploadFn,
    isMobile,
    minHeight,
    visibleToolbarButtons,
    visibleMoreRichButtons,
  ])

  return {
    editorConfig,
    isExpanded,
    isCompact,
    editorContainerRef,
    onChangeRef,
    areGifsAvailable,
    isGifPreferenceLoading,
    giphyDialogId,
    handleGifSelected,
    attachmentHook,
  }
}
